<template>
  <Modal
    v-model:visible="isVisible"
    :header="t('shipments.bolTotalHandlingUnitOverride.title')"
    size="sm"
  >
    <template #default>
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :input-id="`${uid}-handling-unit-override`"
        :label="
          t('shipments.bolTotalHandlingUnitOverride.totalHandlingUnitOverride')
        "
        :errors="v$.overrideValue.$errors"
      >
        <InputNumber
          :id="inputId"
          v-model="overrideValue"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          autofocus
          :placeholder="
            t(
              'shipments.bolTotalHandlingUnitOverride.totalHandlingUnitOverride',
            )
          "
        />
      </InputWrapper>
    </template>

    <template #footer>
      <RygenButton
        mode="secondary"
        :label="
          t('shipments.bolTotalHandlingUnitOverride.skipWithoutOverriding')
        "
        @click="onSkip"
      />
      <RygenButton
        mode="primary"
        :label="t('shipments.bolTotalHandlingUnitOverride.printWithOverride')"
        :disabled="overrideValue == null || v$.overrideValue.$errors.length"
        @click="onPrintWithOverride"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  minValue,
  required,
  useUid,
  useValidations,
} from '@rygen/rygen-ui/composables';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import InputNumber from 'primevue/inputnumber';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const emit = defineEmits<{
  (event: 'override', value: number): void;
  (event: 'skip'): void;
}>();

const { t } = useI18n();
const { uid } = useUid('bol-handling-unit-override-modal');

const overrideValue = ref<number | null>(null);

const formData = computed(() => ({ overrideValue: overrideValue.value }));

const rules = computed(() => ({
  overrideValue: {
    required: required(
      t('shipments.bolTotalHandlingUnitOverride.totalHandlingUnitOverride'),
    ),
    minValue: minValue(
      1,
      t('shipments.bolTotalHandlingUnitOverride.totalHandlingUnitOverride'),
    ),
  },
}));

const { v$, validate } = useValidations(formData, rules, uid);

const onSkip = () => {
  isVisible.value = false;
  emit('skip');
};

const onPrintWithOverride = async () => {
  if (!(await validate())) {
    return;
  }

  isVisible.value = false;
  emit('override', overrideValue.value!);
};

const resetFields = () => {
  overrideValue.value = null;
  v$.value.$reset();
};

watch(isVisible, (newVal: boolean, oldVal: boolean) => {
  if (!newVal && oldVal) {
    resetFields();
  }
});
</script>
